/**
 * Pilotage-plan overlay — draws the whole active plan on the chart as one grouped
 * layer that moves with the chart (real geometry in a GeoJSON source, not
 * screen-space UI): the route and its per-leg time labels, wheel-over bearings,
 * transits, clearing bearings and back bearings, depth-contour follow sections and
 * do-not-cross clearing-depth lines (shallow side shaded light red), freehand
 * sketches, and sticky-note pins.
 *
 * Built to the same proven shape as the range-rings/EBL-VRM layers: `build` turns
 * the plan doc into a GeoJSON FeatureCollection, `ensurePilotagePlanLayer` adds
 * the source + layers once, and `updatePilotagePlanLayer` re-feeds the data. The
 * whole plan hides in one go via each layer's `visibility` (NOT by emptying the
 * source), so the show/hide toggle can never be confused with "there's no data".
 * `update` also re-ensures the layers, so a lifecycle race (source not added yet,
 * or wiped by a style change) can't leave the plan permanently invisible.
 */
import type { Map as MaplibreMap, GeoJSONSource } from 'maplibre-gl';
import { isStyleReady } from './styleReady';
import type { Feature, FeatureCollection } from 'geojson';
import { rhumbBearingDeg, rhumbDestination } from '@/nav';
import { legTimeLabels } from '@/pilotage/plannerTools';
import type { PilotagePlanDoc, LonLat } from '@/pilotage/plannerModel';
import { getPalette } from './style/palettes';
import type { Palette } from '@/store';

const SOURCE_ID = 'pilotage-plan';

/** Every layer this overlay owns (for the one-tap visibility toggle). */
const LAYER_IDS = [
  'plan-cdepth-fill',
  'plan-line-route',
  'plan-line-solid',
  'plan-line-dashed',
  'plan-point',
  'plan-waypoint',
  'plan-note',
  'plan-note-icon',
  'plan-label',
];

/**
 * Lift the pilotage-plan layers to the top of the stack, in draw order, so the
 * plan's route, bearings, contours and notes are never buried. Same reason as the
 * route and ownship layers: the training/vector chart cells load asynchronously, so
 * if the plan layers are created before the cells finish loading, addCellToMap
 * appends the cell layers ABOVE them and the opaque vector depth/land fills cover
 * the plan. moveLayer with no `before` moves a layer to the top, so walking
 * LAYER_IDS bottom -> top leaves the draw order intact. Missing layers are skipped.
 */
export function raisePilotagePlanLayers(map: MaplibreMap): void {
  for (const id of LAYER_IDS) {
    if (map.getLayer(id)) map.moveLayer(id);
  }
}

const recip = (deg: number): number => (deg + 180) % 360;

function distNm(a: LonLat, b: LonLat): number {
  const k = Math.cos((a[1] * Math.PI) / 180);
  const dx = (b[0] - a[0]) * 60 * k;
  const dy = (b[1] - a[1]) * 60;
  return Math.hypot(dx, dy);
}

/** Offset a path to one side by distNm (for the clearing-depth shaded band). */
function offsetPath(path: LonLat[], side: 'left' | 'right', d: number): LonLat[] {
  const out: LonLat[] = [];
  for (let i = 0; i < path.length; i++) {
    const a = path[i];
    const b = path[Math.min(i + 1, path.length - 1)] ?? a;
    const brg = i < path.length - 1 ? rhumbBearingDeg(a, b) : rhumbBearingDeg(path[i - 1] ?? a, a);
    const perp = (brg + (side === 'left' ? 90 : -90) + 360) % 360;
    out.push(rhumbDestination(a, perp, d));
  }
  return out;
}

/** A circle ring of points, for a freehand circle [centre, edge]. */
function ringPoints(centre: LonLat, edge: LonLat, steps = 48): LonLat[] {
  const rNm = Math.max(0.005, distNm(centre, edge));
  const out: LonLat[] = [];
  for (let i = 0; i <= steps; i++) out.push(rhumbDestination(centre, (i / steps) * 360, rNm));
  return out;
}

interface Colors {
  route: string;
  wheelover: string;
  bearing: string;
  contour: string;
  cdepth: string;
  freehand: string;
}

function paletteColors(palette: Palette): Colors {
  const p = getPalette(palette);
  return {
    route: p.UI_ACCENT,
    wheelover: p.CHBLK_YELLOW,
    bearing: p.CHMGD,
    contour: '#1b6ec2',
    cdepth: p.CHBLK_RED,
    freehand: p.CHBLK_GREEN,
  };
}

/**
 * Build the plan's GeoJSON. ALWAYS emits every element (visibility is handled by
 * the layers, not here), so the source is never mistakenly emptied.
 */
export function buildPlanFeatures(doc: PilotagePlanDoc | null, c: Colors): FeatureCollection {
  const f: Feature[] = [];
  if (!doc) return { type: 'FeatureCollection', features: f };

  const line = (coords: LonLat[], role: string, color: string) =>
    f.push({ type: 'Feature', geometry: { type: 'LineString', coordinates: coords }, properties: { role, color } });
  const label = (pos: LonLat, text: string, color: string) =>
    f.push({ type: 'Feature', geometry: { type: 'Point', coordinates: pos }, properties: { kind: 'label', text, color } });
  const dot = (pos: LonLat, color: string) =>
    f.push({ type: 'Feature', geometry: { type: 'Point', coordinates: pos }, properties: { kind: 'dot', color } });

  // Clearing-depth shaded band (under everything).
  for (const cd of doc.clearingDepths) {
    if (cd.path.length >= 2) {
      const band = offsetPath(cd.path, cd.shallowSide, 0.06);
      f.push({
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [[...cd.path, ...band.slice().reverse(), cd.path[0]]] },
        properties: { kind: 'cdepth-fill' },
      });
      line(cd.path, 'dashed', c.cdepth);
      label(cd.path[Math.floor(cd.path.length / 2)], cd.depthM != null ? `${cd.depthM} m — keep to seaward` : 'do not cross', c.cdepth);
    }
  }

  // Contour-follow sections.
  for (const cf of doc.contourFollows) {
    if (cf.path.length >= 2) {
      line(cf.path, 'solid', c.contour);
      if (cf.depthM != null) label(cf.path[Math.floor(cf.path.length / 2)], `${cf.depthM} m contour`, c.contour);
    }
  }

  // Route + per-leg time labels (geographic midpoints; no screen projection).
  const wps = doc.route.waypoints.map((w) => w.position);
  if (wps.length >= 2) {
    line(wps, 'route', c.route);
    for (const lg of legTimeLabels(doc.route.waypoints, doc.route.baseSpeedKn)) label(lg.position, lg.text, c.route);
  }
  // Route waypoints (own layer for hit-testing / dragging).
  for (const w of doc.route.waypoints) {
    f.push({ type: 'Feature', geometry: { type: 'Point', coordinates: w.position }, properties: { kind: 'wp', id: w.id, name: w.name ?? '' } });
  }

  // Wheel-over bearings.
  for (const wo of doc.wheelOvers) {
    const wp = doc.route.waypoints.find((w) => w.id === wo.waypointId);
    if (wp) {
      line([wp.position, wo.target], 'dashed', c.wheelover);
      dot(wo.target, c.wheelover);
      label(rhumbDestination(wp.position, rhumbBearingDeg(wp.position, wo.target), Math.max(0.03, distNm(wp.position, wo.target) * 0.5)), `WO ${Math.round(wo.bearingDeg)}°`, c.wheelover);
    }
  }

  // Transits.
  for (const t of doc.transits) {
    const far = rhumbDestination(t.front, rhumbBearingDeg(t.rear, t.front), 0.6);
    line([t.rear, far], 'solid', c.bearing);
    dot(t.front, c.bearing);
    dot(t.rear, c.bearing);
    label(t.front, `${t.frontLabel ?? 'transit'} ${Math.round(rhumbBearingDeg(t.rear, t.front))}°`, c.bearing);
  }

  // Clearing bearings.
  for (const cb of doc.clearingBearings) {
    const freeEnd = rhumbDestination(cb.anchor, recip(cb.bearingDeg), Math.max(0.05, cb.lengthNm));
    const farEnd = rhumbDestination(cb.anchor, cb.bearingDeg, 0.3);
    line([freeEnd, farEnd], 'solid', c.bearing);
    dot(cb.anchor, c.bearing);
    label(freeEnd, `NLT/NMT ${Math.round(cb.bearingDeg)}°`, c.bearing);
  }

  // Heading / back bearings.
  for (const bb of doc.backBearings) {
    const vesselEnd = rhumbDestination(bb.anchor, recip(bb.bearingDeg), Math.max(0.05, bb.lengthNm));
    line([vesselEnd, bb.anchor], 'dashed', c.bearing);
    dot(bb.anchor, c.bearing);
    label(vesselEnd, `${bb.kind === 'back' ? 'Back' : 'Head'} ${Math.round(bb.bearingDeg)}°`, c.bearing);
  }

  // Freehand.
  for (const fh of doc.freehand) {
    if (fh.kind === 'circle' && fh.points.length >= 2) {
      line(ringPoints(fh.points[0], fh.points[1]), 'solid', c.freehand);
    } else if (fh.points.length >= 2) {
      line(fh.points, 'solid', c.freehand);
      if (fh.kind === 'arrow') {
        const n = fh.points.length;
        const brg = rhumbBearingDeg(fh.points[n - 2], fh.points[n - 1]);
        const tip = fh.points[n - 1];
        line([rhumbDestination(tip, (brg + 150) % 360, 0.03), tip, rhumbDestination(tip, (brg + 210) % 360, 0.03)], 'solid', c.freehand);
      }
    }
  }

  // Sticky notes.
  for (const n of doc.stickyNotes) {
    f.push({ type: 'Feature', geometry: { type: 'Point', coordinates: n.position }, properties: { kind: 'note', id: n.id } });
  }

  return { type: 'FeatureCollection', features: f };
}

export function ensurePilotagePlanLayer(map: MaplibreMap, palette: Palette): void {
  // Style-spec-ready gate (not isStyleLoaded(), which the running sim keeps false):
  // addSource/addLayer throw "Style is not done loading" before the style parses.
  if (!isStyleReady(map)) return;
  const p = getPalette(palette);
  if (!map.getSource(SOURCE_ID)) {
    map.addSource(SOURCE_ID, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  }
  if (!map.getLayer('plan-cdepth-fill')) {
    map.addLayer({ id: 'plan-cdepth-fill', type: 'fill', source: SOURCE_ID, filter: ['==', ['get', 'kind'], 'cdepth-fill'], paint: { 'fill-color': p.CHBLK_RED, 'fill-opacity': 0.16 } });
  }
  if (!map.getLayer('plan-line-route')) {
    map.addLayer({ id: 'plan-line-route', type: 'line', source: SOURCE_ID, filter: ['==', ['get', 'role'], 'route'], layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': ['get', 'color'], 'line-width': 3, 'line-opacity': 0.95 } });
  }
  if (!map.getLayer('plan-line-solid')) {
    map.addLayer({ id: 'plan-line-solid', type: 'line', source: SOURCE_ID, filter: ['==', ['get', 'role'], 'solid'], paint: { 'line-color': ['get', 'color'], 'line-width': 2, 'line-opacity': 0.95 } });
  }
  if (!map.getLayer('plan-line-dashed')) {
    map.addLayer({ id: 'plan-line-dashed', type: 'line', source: SOURCE_ID, filter: ['==', ['get', 'role'], 'dashed'], paint: { 'line-color': ['get', 'color'], 'line-width': 2, 'line-opacity': 0.95, 'line-dasharray': [2, 1.5] } });
  }
  if (!map.getLayer('plan-point')) {
    map.addLayer({ id: 'plan-point', type: 'circle', source: SOURCE_ID, filter: ['==', ['get', 'kind'], 'dot'], paint: { 'circle-radius': 3.5, 'circle-color': ['get', 'color'], 'circle-stroke-color': p.UI_BG, 'circle-stroke-width': 1 } });
  }
  if (!map.getLayer('plan-waypoint')) {
    map.addLayer({ id: 'plan-waypoint', type: 'circle', source: SOURCE_ID, filter: ['==', ['get', 'kind'], 'wp'], paint: { 'circle-radius': 6, 'circle-color': p.UI_ACCENT, 'circle-stroke-color': '#ffffff', 'circle-stroke-width': 2 } });
  }
  if (!map.getLayer('plan-note')) {
    map.addLayer({ id: 'plan-note', type: 'circle', source: SOURCE_ID, filter: ['==', ['get', 'kind'], 'note'], paint: { 'circle-radius': 7, 'circle-color': p.CHBLK_YELLOW, 'circle-stroke-color': p.CHBLK, 'circle-stroke-width': 1 } });
  }
  if (!map.getLayer('plan-note-icon')) {
    map.addLayer({ id: 'plan-note-icon', type: 'symbol', source: SOURCE_ID, filter: ['==', ['get', 'kind'], 'note'], layout: { 'icon-image': 'plot-note', 'icon-size': 0.55, 'icon-allow-overlap': true } });
  }
  if (!map.getLayer('plan-label')) {
    map.addLayer({
      id: 'plan-label',
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'label'],
      layout: { 'text-field': ['get', 'text'], 'text-font': ['Open Sans Bold'], 'text-size': 11, 'text-allow-overlap': false, 'text-offset': [0, -0.9] },
      paint: { 'text-color': ['get', 'color'], 'text-halo-color': p.UI_BG, 'text-halo-width': 1.6 },
    });
  }

  // Whether we just added the layers or they already existed (created early, before
  // the async chart cells loaded), keep them above the cells so nothing hides behind
  // the vector chart fills. Same treatment as ensureRouteLayer.
  raisePilotagePlanLayers(map);
}

export function updatePilotagePlanLayer(map: MaplibreMap, doc: PilotagePlanDoc | null, visible: boolean, palette: Palette): void {
  // No-op until the style spec is parsed, so a render effect that fires during a
  // cold load can't call addSource/addLayer and crash the app.
  if (!isStyleReady(map)) return;
  // Re-ensure so a lifecycle race (or a style reload) can't leave us without a
  // source to feed — this is what stops the plan being permanently invisible.
  ensurePilotagePlanLayer(map, palette);
  const src = map.getSource(SOURCE_ID);
  if (src) (src as GeoJSONSource).setData(buildPlanFeatures(doc, paletteColors(palette)));
  // Show/hide the whole plan via layer visibility (data stays in the source).
  const vis = visible && doc != null ? 'visible' : 'none';
  for (const id of LAYER_IDS) {
    if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', vis);
  }
}
